import React, { useState } from "react";
import { useData } from "../../hooks/useData.js";
import { apiFetch } from "../../api/index.js";
import { Button, Modal, FormInput, LoadingSpinner, ConfirmModal } from "../../components/Common.jsx";
import { DynamicIcon } from "../../components/icons.jsx";
import { useNotification } from "../../context/NotificationContext.jsx";

const SupportTiersTab = ({ token }) => {
  const { data: tiers, isLoading, refresh, error } = useData("/plans/support-tiers", token);
  const { addNotification } = useNotification();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const [form, setForm] = useState({
    tierName: "",
    description: "",
    sla: ""
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ tierName: "", description: "", sla: "" });
    setModalOpen(true);
  };

  const openEdit = (tier) => {
    setEditing(tier);
    setForm({ tierName: tier.tierName || "", description: tier.description || "", sla: tier.sla || "" });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing ? `/plans/support-tiers/${editing.supportTierId}` : "/plans/support-tiers";

      await apiFetch(url, token, {
        method,
        body: JSON.stringify(form)
      });

      addNotification(editing ? "Support tier updated" : "Support tier created");
      setModalOpen(false);
      refresh();
    } catch (err) {
      addNotification(err?.message || "Failed to save support tier", "error");
    }
  };

  const handleDelete = async (t) => {
    try {
      await apiFetch(`/plans/support-tiers/${t.supportTierId}`, token, { method: "DELETE" });
      addNotification("Support tier deleted");
      refresh();
    } catch (err) {
      addNotification(err?.message || "Failed to delete support tier", "error");
    } finally {
      setDeleteItem(null);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Failed to load support tiers</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Support Tiers</h2>
        <Button onClick={openCreate}>
          <DynamicIcon name="PlusCircle" className="mr-2" /> Add Support Tier
        </Button>
      </div>

      <div className="space-y-3">
        {tiers.map(t => (
          <div key={t.supportTierId} className="p-4 border rounded-lg bg-white dark:bg-gray-800 flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-lg">{t.tierName}</h3>
              <p className="text-sm text-gray-600">{t.description}</p>
              <p className="text-xs mt-1">SLA: {t.sla}</p>
            </div>

            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => openEdit(t)}>
                <DynamicIcon name="Edit" />
              </Button>
              <Button variant="danger" onClick={() => setDeleteItem(t)}>
                <DynamicIcon name="Trash2" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={modalOpen} title={editing ? "Edit Support Tier" : "Create Support Tier"} onClose={() => setModalOpen(false)}>
        <div className="space-y-4">
          <FormInput
            label="Tier Name"
            value={form.tierName}
            onChange={(e) => setForm({ ...form, tierName: e.target.value })}
          />

          <FormInput
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <FormInput
            label="SLA"
            placeholder="Example: 24 Hours / 99.9% / Priority Response"
            value={form.sla}
            onChange={(e) => setForm({ ...form, sla: e.target.value })}
          />

          <div className="flex justify-end">
            <Button onClick={handleSave}>
              {editing ? "Save Changes" : "Create Tier"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteItem}
        title={`Delete ${deleteItem?.tierName}?`}
        onClose={() => setDeleteItem(null)}
        onConfirm={() => handleDelete(deleteItem)}
      >
        Are you sure you want to delete this support tier?
      </ConfirmModal>
    </div>
  );
};

export default SupportTiersTab;
